import { Divider, Grid, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NothingFound from 'common/components/NothingFound';
import PropTypes from 'prop-types';
import { capitalize, isEmpty, map } from 'lodash';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import { ItemLabelValue } from './PrescriptionsTab/PatientPrescriptionCustomLabels';
import PatientPrescriptionsTable from './PrescriptionsTab/PatientPrescriptionsTable';

const PatientInfoCardView = ({ patientInfo, heading, isSource }) => {
  const theme = useTheme();

  return (
    <Grid
      sx={{
        width: '100%',
        height: '100%',
        bgcolor: 'background.paper',
        boxShadow: 3,
        borderRadius: 2,
        overflowY: 'auto',
        p: 1,
        border: `1px solid ${theme.palette.divider}`,
        ...(!isSource && { background: '#f5f5f5ff' }),
      }}
    >
      {isEmpty(patientInfo) && (
        <NothingFound
          customMessage='No patient records selected. Select a record to begin comparison.'
          hideBox
        />
      )}
      {!isEmpty(patientInfo) && (
        <Grid container direction='column' rowGap={2} sx={{ paddingLeft: '5px' }}>
          <Grid container direction='column' item>
            <Grid container direction='row' alignItems='center'>
              <Grid item>
                <Typography variant='h6' fontWeight='bold' sx={{ fontWeight: 600, pb: 1 }}>
                  {heading}
                </Typography>
              </Grid>
              <Grid item sx={{ marginLeft: '15px' }}>
                <Tooltip
                  title={
                    isSource
                      ? 'You’re seeing this patient because you opened the duplicate check from their profile.'
                      : 'Displayed because you selected this patient for comparison.'
                  }
                  placement='top'
                  arrow
                >
                  <span>
                    <InfoOutlineIcon sx={{ cursor: 'pointer' }} />
                  </span>
                </Tooltip>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 1 }} />
            <Typography variant='h6' fontWeight='bold' sx={{ fontWeight: 600 }}>
              Patient
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <ItemLabelValue
              label='Patient First Name'
              value={capitalize(patientInfo?.patient?.firstName)}
            />
            <ItemLabelValue
              label='Patient Last Name'
              value={capitalize(patientInfo?.patient?.lastName)}
            />
            <ItemLabelValue label='DOB' value={patientInfo?.patient?.dob} />
            <ItemLabelValue label='Gender' value={capitalize(patientInfo?.patient?.gender)} />
            <ItemLabelValue
              label='Created At'
              value={new Date(patientInfo?.patient?.createdTime).toLocaleString()}
            />
          </Grid>

          <Grid container direction='column' item>
            <Typography variant='h6' fontWeight='bold' sx={{ fontWeight: 600 }}>
              Practice
            </Typography>
            <Divider sx={{ mb: 1 }} />

            {map(patientInfo?.practice, (record, index) => (
              <ItemLabelValue
                key={index}
                label={`Practice Name #${index + 1}`}
                value={capitalize(record?.practiceName) ?? 'NA'}
              />
            ))}
          </Grid>

          <Grid container direction='column' item>
            <Typography variant='h6' fontWeight='bold' sx={{ fontWeight: 600 }}>
              Partner
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <ItemLabelValue
              label='Partner Name'
              value={capitalize(patientInfo?.partner?.partnerName) ?? 'NA'}
            />
            <ItemLabelValue label='External Id' value={patientInfo?.externalId ?? 'NA'} />
          </Grid>

          <Grid container direction='column' item>
            <Typography variant='h6' fontWeight='bold' sx={{ fontWeight: 600 }}>
              Prescriptions
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <PatientPrescriptionsTable mpi={patientInfo?.patientId} hideOptions />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default PatientInfoCardView;

PatientInfoCardView.propTypes = {
  patientInfo: PropTypes.shape({
    partner: PropTypes.shape({
      partnerName: PropTypes.string,
    }),
    patient: PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      createdTime: PropTypes.string.isRequired,
      dob: PropTypes.string.isRequired,
      gender: PropTypes.string.isRequired,
    }),
    externalId: PropTypes.string,
    patientId: PropTypes.string.isRequired,
    practice: PropTypes.arrayOf(
      PropTypes.shape({
        practiceName: PropTypes.string,
      })
    ),
  }),
  heading: PropTypes.string.isRequired,
  isSource: PropTypes.bool,
};

PatientInfoCardView.defaultProps = {
  patientInfo: {
    partner: {
      partnerName: null,
    },
    externalId: null,
  },
  isSource: false,
};
